// Updated to use new BLOB-based endpoints exposed by Flask at /api/photos_blob/* (proxied via Node)
// Endpoints:
//  POST   /api/photos_blob/upload (form-data: employee_id, photo)
//  GET    /api/photos_blob/list/:employee_id -> { employee_id, photos: [{id, image_base64, created_at}] }
//  DELETE /api/photos_blob/delete/:photo_id
// Constraints: max 10 photos per employee; duplicate detection (409) via SHA256 hash.
// This component batch-uploads selected files sequentially so each duplicate / error is reported.

import React, { useState, useEffect } from "react";
import axios from "axios";
import { formatDateOnly, formatDateTimeToTime12h } from "../utils/dateTimeUtils";

const API_BASE = process.env.REACT_APP_API_BASE || '';

export default function PhotoUploader({ employeeId, employeeName }) {
  const [photos, setPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // 'info' | 'success' | 'error'
  const [progress, setProgress] = useState(0); // overall upload progress 0-100
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [faceServiceOfflineUntil, setFaceServiceOfflineUntil] = useState(0);
  const MAX_PHOTOS = 10;
  const COOLDOWN_MS = 10000;
  const EMPTY_STATE_MESSAGE = 'No photos uploaded yet.';
  const formatDateTime = (value) => value ? `${formatDateOnly(value)} ${formatDateTimeToTime12h(value)}`.trim() : '';

  const isFaceServiceOffline = faceServiceOfflineUntil && Date.now() < faceServiceOfflineUntil;
  const markFaceServiceOffline = (msg) => {
    setFaceServiceOfflineUntil(Date.now() + COOLDOWN_MS);
    setMessage(msg || 'Photo service is offline. Existing photos cannot be loaded right now.');
    setMessageType('error');
  };

  useEffect(() => {
    if (employeeId) fetchPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchPhotos = async () => {
    if (!employeeId) return;
    if (isFaceServiceOffline) {
      setPhotos([]);
      if (!message) {
        setMessage('Photo service is offline. Existing photos cannot be loaded right now.');
        setMessageType('error');
      }
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/api/photos_blob/list/${employeeId}`, { withCredentials: true, validateStatus: () => true });
      if (res.status === 200) {
        const nextPhotos = Array.isArray(res.data?.photos) ? res.data.photos : [];
        setPhotos(nextPhotos);
        if (nextPhotos.length === 0) {
          setMessage(EMPTY_STATE_MESSAGE);
          setMessageType("info");
        } else if (!loading && (message === EMPTY_STATE_MESSAGE || messageType === 'error')) {
          setMessage("");
          setMessageType("info");
        }
        return;
      }
      if (res.status === 404 && /no photos/i.test(String(res.data?.error || res.data?.message || ''))) {
        setPhotos([]);
        setMessage(EMPTY_STATE_MESSAGE);
        setMessageType("info");
        return;
      }
      if (res.status === 503 && res.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
        console.warn('[PhotoUploader] face service unavailable (503)');
        markFaceServiceOffline('Photo service is offline. Existing photos cannot be loaded right now.');
        return;
      }
      console.error("Fetch photos failed:", res.status, res.data?.error || res.statusText);
      setMessage("Failed to load photos.");
      setMessageType("error");
    } catch (err) {
      console.error("Fetch photos exception:", err.message);
      if (err?.response?.status === 503 && err?.response?.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
        markFaceServiceOffline('Photo service is offline. Existing photos cannot be loaded right now.');
      } else {
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Service unavailable; please try again later.";
        setMessage(msg);
        setMessageType("error");
      }
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
    // clear previous message on new selection
    setMessage("");
    setMessageType("info");
  };

  const uploadPreparedFile = async (processedFile, { forceDuplicateUpload = false } = {}) => {
    const fd = new FormData();
    fd.append("employee_id", employeeId);
    if (forceDuplicateUpload) fd.append("force_duplicate_upload", "1");
    fd.append("photo", processedFile);
    return axios.post(`${API_BASE}/api/photos_blob/upload`, fd, {
      withCredentials: true,
      validateStatus: () => true,
      maxBodyLength: Infinity,
      onUploadProgress: (evt) => {
        try {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setProgress((prev) => Math.max(prev, pct));
          }
        } catch (_) {}
      }
    });
  };

  const handleUploadAndRegister = async () => {
    console.log('[PhotoUploader] upload clicked', { employeeId });
    setMessage('Upload clicked...');
    setMessageType('info');
    if (isFaceServiceOffline) {
      setMessage('Photo service is offline. Please try again shortly.');
      setMessageType('error');
      return;
    }
    if (!employeeId) {
      setMessage(`No employee selected. employeeId=${employeeId}`);
      setMessageType("error");
      return;
    }
    if (!selectedFiles.length) {
      setMessage("Please select at least one photo.");
      setMessageType("error");
      return;
    }
    if (photos.length >= MAX_PHOTOS) {
      setMessage(`Max of ${MAX_PHOTOS} photos already reached.`);
      setMessageType("error");
      return;
    }
    const remainingSlots = MAX_PHOTOS - photos.length;
    const filesToUpload = selectedFiles.slice(0, remainingSlots);
    if (filesToUpload.length < selectedFiles.length) {
      setMessage(`Only ${filesToUpload.length} file(s) will be uploaded (limit ${MAX_PHOTOS}).`);
      setMessageType("info");
    } else {
      setMessage("Uploading...");
      setMessageType("info");
    }
    setLoading(true);
    let success = 0;
    let duplicates = 0;
    let failed = 0;
    try {
      for (const file of filesToUpload) {
        // Client-side downscale/compress to accelerate upload
        const processed = await prepareImage(file);
        const res = await uploadPreparedFile(processed);
        if (res.status === 201 || res.status === 200) {
          success += 1;
        } else if (res.status === 409 && res.data?.code === 'POTENTIAL_DUPLICATE_FACE') {
          setDuplicateWarning({
            processedFile: processed,
            duplicate: res.data?.duplicate || null,
            message: res.data?.message || 'Possible duplicate face detected.',
          });
          setLoading(false);
          setProgress(0);
          setMessage(res.data?.message || 'Possible duplicate face detected.');
          setMessageType("info");
          return;
        } else if (res.status === 409) {
          duplicates += 1;
        } else if (res.status === 413) {
          failed += 1;
          setMessage("File too large (max 10MB)");
          setMessageType("error");
        } else if (res.status === 415) {
          failed += 1;
          setMessage("Unsupported file type (jpeg/png/gif/webp only)");
          setMessageType("error");
        } else if (res.status === 503) {
          failed += 1;
          if (res.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
            markFaceServiceOffline('Photo service is offline. Please try again shortly.');
          } else {
            setMessage("Service unavailable (face service down)");
            setMessageType("error");
          }
        } else if (res.status === 403) {
          failed += 1;
          setMessage("Forbidden: you can only manage your own photos");
          setMessageType("error");
        } else if (res.status === 400 && res.data?.error?.includes('max photos')) {
          throw new Error('MAX_PHOTOS_REACHED');
        } else if (res.status === 400) {
          failed += 1;
          const backendError = String(res.data?.error || res.data?.message || '').trim();
          setMessage(backendError || "Upload failed.");
          setMessageType("error");
        } else {
          failed += 1;
        }
      }
    } catch (e) {
      if (!(e && e.message === 'MAX_PHOTOS_REACHED')) {
        failed += 1;
      }
    }
  setSelectedFiles([]);
    setLoading(false);
  setProgress(0);
  await fetchPhotos();
    const parts = [];
    if (success) parts.push(`${success} uploaded`);
    if (duplicates) parts.push(`${duplicates} duplicate` + (duplicates > 1 ? 's' : ''));
    if (failed) parts.push(`${failed} failed`);
    await rebuildEncodings(false);
    if (!parts.length) parts.push('No files processed');
    setMessage(parts.join(' | '));
    setMessageType(failed ? "error" : (success ? "success" : "info"));
  };

  const handleProceedDuplicateUpload = async () => {
    if (!duplicateWarning?.processedFile) return;
    setLoading(true);
    setProgress(0);
    try {
      const res = await uploadPreparedFile(duplicateWarning.processedFile, { forceDuplicateUpload: true });
      if (res.status === 201 || res.status === 200) {
        await fetchPhotos();
        await rebuildEncodings(false);
        setMessage("Photo uploaded after duplicate review.");
        setMessageType("success");
        setSelectedFiles([]);
      } else {
        const backendError = String(res.data?.error || res.data?.message || '').trim();
        setMessage(backendError || "Upload failed.");
        setMessageType("error");
      }
    } catch (err) {
      setMessage("Upload failed.");
      setMessageType("error");
    } finally {
      setLoading(false);
      setProgress(0);
      setDuplicateWarning(null);
    }
  };

  const handleDelete = async (photoId) => {
    setConfirmDeleteId(photoId);
  };

  const performDelete = async () => {
    const photoId = confirmDeleteId;
    if (!photoId) return;
    try {
      await axios.delete(`${API_BASE}/api/photos_blob/delete/${photoId}`, { withCredentials: true, params: { employeeid: employeeId } });
      setMessage("Photo deleted");
      setMessageType("success");
      setConfirmDeleteId(null);
      await fetchPhotos();
    } catch (err) {
      console.error("Delete failed:", err.message);
      if (err?.response?.status === 503 && err?.response?.data?.error === 'FACE_SERVICE_UNAVAILABLE') {
        markFaceServiceOffline('Photo service is offline. Existing photos cannot be loaded right now.');
      } else {
        setMessage("Failed to delete photo.");
        setMessageType("error");
      }
      setConfirmDeleteId(null);
    }
  };

  const rebuildEncodings = async (showMessage) => {
    if (!employeeId) {
      if (showMessage) {
        setMessage(`No employee selected. employeeId=${employeeId}`);
        setMessageType("error");
      }
      return null;
    }
    try {
      if (showMessage) {
        setMessage("Rebuilding encodings...");
        setMessageType("info");
      }
      const res = await axios.post(`${API_BASE}/api/encodings/rebuild`, { employee_id: employeeId }, { withCredentials: true, validateStatus: () => true });
      const data = (res && res.data && typeof res.data === 'object') ? res.data : null;
      if (showMessage) {
        if (data) {
          setMessage(data.ok ? "Encodings rebuilt." : "Rebuild failed.");
          setMessageType(data.ok ? "success" : "error");
        } else {
          setMessage("Rebuild failed.");
          setMessageType("error");
        }
      }
      return data;
    } catch (err) {
      if (showMessage) {
        setMessage("Rebuild failed.");
        setMessageType("error");
      }
      return null;
    }
  };

  // Downscale image to max 1024px and compress to JPEG ~0.7 quality for speed.
  async function prepareImage(file) {
    const MAX_DIM = 1024;
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) return resolve(file); // skip non-images
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        try {
          let { width, height } = img;
          if (width <= MAX_DIM && height <= MAX_DIM) {
            URL.revokeObjectURL(url);
            return resolve(file); // already small
          }
          const scale = Math.min(MAX_DIM / width, MAX_DIM / height);
          const newW = Math.round(width * scale);
          const newH = Math.round(height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = newW;
          canvas.height = newH;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, newW, newH);
          canvas.toBlob((blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return resolve(file);
            const optimized = new File([blob], file.name.replace(/\.(png|jpg|jpeg)$/i, '_opt.jpg'), { type: 'image/jpeg' });
            resolve(optimized);
          }, 'image/jpeg', 0.7);
        } catch (e) {
          URL.revokeObjectURL(url);
          resolve(file); // fallback
        }
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  return (
    <div>
  <h3>Employee Photos</h3>
      <p><strong>{employeeName || employeeId}</strong> – {photos.length} / {MAX_PHOTOS} photo(s)</p>
      <div style={{ marginBottom: '10px' }}>
        <input type="file" multiple accept="image/*" onChange={handleFileChange} />
        <button
          onClick={handleUploadAndRegister}
          disabled={loading}
          style={{
            marginLeft: '10px',
            background: '#0066ff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            padding: '8px 14px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Uploading...' : 'UPLOAD PHOTOS'}
        </button>
      </div>
      {loading && (
        <div style={{ width: '100%', height: 8, background: '#eee', borderRadius: 4, overflow: 'hidden', margin: '6px 0' }}>
          <div style={{ width: `${progress}%`, height: '100%', background: '#4caf50', transition: 'width 0.2s ease' }} />
        </div>
      )}
      {!employeeId && (
        <p style={{ color: '#c62828', marginTop: '5px' }}>Missing employeeId. Current value: {String(employeeId)}</p>
      )}
  {message && message !== EMPTY_STATE_MESSAGE && (
    <p style={{ color: messageType === 'error' ? '#c62828' : (messageType === 'success' ? '#2e7d32' : 'gray'), marginTop: '5px' }}>{message}</p>
  )}
      {!loading && photos.length === 0 && !isFaceServiceOffline && (
        <p style={{ color: '#6b7280', marginTop: '10px' }}>{EMPTY_STATE_MESSAGE}</p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '10px' }}>
        {photos.map(p => (
          <div key={p.id} style={{ margin: '8px', textAlign: 'center', border: '1px solid #ddd', padding: '5px', borderRadius: '8px', width: 140 }}>
            <div style={{ width: 120, height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7f7f7', borderRadius: 6, overflow: 'hidden' }}>
              <img
                src={`${API_BASE}/api/photos_blob/image/${p.id}`}
                alt="face"
                width="120"
                height="120"
                loading="lazy"
                style={{ borderRadius: '6px', objectFit: 'cover', display: 'block' }}
                onError={(e) => { e.currentTarget.style.opacity = 0.3; e.currentTarget.alt = 'failed'; }}
              />
            </div>
            <small style={{ display: 'block', fontSize: '10px', opacity: 0.6 }}>{formatDateTime(p.created_at)}</small>
            <button
              style={{
                background: 'red',
                color: 'white',
                border: 'none',
                marginTop: '5px',
                cursor: 'pointer',
                borderRadius: '4px',
                padding: '4px 8px'
              }}
              onClick={() => handleDelete(p.id)}
            >Delete</button>
          </div>
        ))}
      </div>

      {/* Lightweight confirmation overlay */}
      {confirmDeleteId !== null && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{ background: '#1f2937', color: '#fff', padding: '16px 18px', borderRadius: 8, minWidth: 280, boxShadow: '0 10px 24px rgba(0,0,0,0.35)'}}>
            <div style={{ fontSize: 16, marginBottom: 10 }}>Delete this photo?</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{ background: '#374151', color: '#e5e7eb', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
              >Cancel</button>
              <button
                onClick={performDelete}
                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
              >Delete</button>
            </div>
          </div>
        </div>
      )}

      {duplicateWarning && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{ background: '#ffffff', color: '#111827', padding: '18px 20px', borderRadius: 10, minWidth: 340, maxWidth: 620, boxShadow: '0 10px 24px rgba(0,0,0,0.35)' }}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Possible Duplicate Face Detected</div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
              {duplicateWarning.message}
            </div>
            {duplicateWarning.duplicate && (
              <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: '120px 1fr', gap: 14, alignItems: 'start' }}>
                <div style={{ width: 120, height: 120, background: '#f3f4f6', borderRadius: 8, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {duplicateWarning.duplicate.photo_id ? (
                    <img
                      src={`${API_BASE}/api/photos_blob/image/${duplicateWarning.duplicate.photo_id}`}
                      alt="Possible duplicate"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <span style={{ color: '#6b7280', fontSize: 12, textAlign: 'center', padding: 8 }}>No preview photo</span>
                  )}
                </div>
                <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.7 }}>
                  <div><strong>Account:</strong> {duplicateWarning.duplicate.full_name || 'Unknown'}</div>
                  <div><strong>Employee ID:</strong> {duplicateWarning.duplicate.employeeid || duplicateWarning.duplicate.employee_no || 'N/A'}</div>
                  <div><strong>Role:</strong> {duplicateWarning.duplicate.role || 'N/A'}</div>
                  <div><strong>Similarity:</strong> {typeof duplicateWarning.duplicate.confidence === 'number' ? `${Math.round(duplicateWarning.duplicate.confidence * 100)}%` : 'N/A'}</div>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                onClick={() => setDuplicateWarning(null)}
                style={{ background: '#e5e7eb', color: '#111827', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
              >
                Upload Another Photo
              </button>
              <button
                onClick={handleProceedDuplicateUpload}
                style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6, cursor: 'pointer' }}
              >
                Proceed Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
